import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useSubscriptionCheck } from '../components/SubscriptionGate';

export default function DirectorySubmit() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const { checkAdLimit, incrementAdCount } = useSubscriptionCheck();
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    category: '',
    categories: [],  // NEW: Multiple categories
    description_fr: '',
    description_en: '',
    country: '',
    city: '',
    address: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    logo: '',
    services: [],
    certifications: [],
    company_size: ''
  });

  // NEW: Toggle category in multiple categories array
  const toggleCategory = (categoryId) => {
    setFormData(prev => {
      const currentCategories = prev.categories || [];
      const newCategories = currentCategories.includes(categoryId)
        ? currentCategories.filter(c => c !== categoryId)
        : [...currentCategories, categoryId];
      
      return {
        ...prev,
        categories: newCategories,
        // Keep primary category as first selected, for backward compatibility
        category: newCategories.length > 0 ? newCategories[0] : ''
      };
    });
  };

  const fetchCategories = async () => {
    try {
      // Fetch new hierarchical categories
      const response = await axios.get(`${API}/directory/families`);
      const families = response.data;
      
      // Flatten categories from all families for the select dropdown
      const allCategories = [];
      for (const family of families) {
        try {
          const familyResponse = await axios.get(`${API}/directory/families/${family.slug}`);
          if (familyResponse.data.categories) {
            for (const cat of familyResponse.data.categories) {
              allCategories.push({
                id: cat.slug,
                name_fr: `${family.name_fr} > ${cat.name_fr}`,
                family_slug: family.slug
              });
            }
          }
        } catch (e) {
          console.error(`Error fetching family ${family.slug}:`, e);
        }
      }
      setCategories(allCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/directory/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchCategories();
      await fetchCountries();
    };
    loadData();
  }, [API]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check subscription limit before submitting
    const check = await checkAdLimit('directory');
    if (!check.allowed) {
      toast.error(check.message || 'Limite d\'annonces atteinte', {
        description: 'Passez à un plan supérieur pour ajouter votre entreprise',
        action: { label: 'Voir les offres', onClick: () => navigate('/pricing') }
      });
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/directory/professionals/submit`, formData);
      
      // Increment subscription ad count for new directory entry
      await incrementAdCount('directory');
      
      toast.success(
        i18n.language === 'fr'
          ? 'Votre fiche a été soumise avec succès ! Elle sera vérifiée par notre équipe.'
          : 'Your listing has been submitted successfully! It will be reviewed by our team.'
      );
      
      navigate('/directory');
    } catch (error) {
      console.error('Error submitting listing:', error);
      toast.error(
        error.response?.data?.detail || 
        (i18n.language === 'fr' ? 'Erreur lors de la soumission' : 'Error submitting listing')
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(
        i18n.language === 'fr' 
          ? 'Le fichier est trop volumineux (max 5MB)' 
          : 'File is too large (max 5MB)'
      );
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error(
        i18n.language === 'fr' 
          ? 'Le fichier doit être une image' 
          : 'File must be an image'
      );
      return;
    }

    setUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/directory/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, logo: response.data.logo_url }));
      toast.success(
        i18n.language === 'fr' 
          ? 'Logo uploadé avec succès' 
          : 'Logo uploaded successfully'
      );
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(
        i18n.language === 'fr' 
          ? 'Erreur lors de l\'upload du logo' 
          : 'Error uploading logo'
      );
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl text-[#0A2540]">
              {i18n.language === 'fr' ? "Inscrire mon entreprise dans l'annuaire" : 'Add My Company to Directory'}
            </CardTitle>
            <p className="text-gray-600 mt-2">
              {i18n.language === 'fr' 
                ? 'Votre fiche sera vérifiée avant publication' 
                : 'Your listing will be reviewed before publication'}
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-[#0A2540]">
                  {i18n.language === 'fr' ? 'Informations de l\'entreprise' : 'Company Information'}
                </h3>
                
                <div>
                  <Label htmlFor="company_name">
                    {i18n.language === 'fr' ? 'Nom de l\'entreprise' : 'Company Name'} *
                  </Label>
                  <Input
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="logo">
                    {i18n.language === 'fr' ? 'Logo de l\'entreprise' : 'Company Logo'}
                  </Label>
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {i18n.language === 'fr' 
                      ? 'Format: JPG, PNG, WebP (max 5MB)' 
                      : 'Format: JPG, PNG, WebP (max 5MB)'}
                  </p>
                  {uploadingLogo && (
                    <p className="text-sm text-[#635BFF] mt-2">
                      {i18n.language === 'fr' ? 'Upload en cours...' : 'Uploading...'}
                    </p>
                  )}
                  {formData.logo && (
                    <div className="mt-3">
                      <img 
                        src={`${API}${formData.logo}`} 
                        alt="Logo preview" 
                        className="w-32 h-32 object-contain border rounded-lg p-2"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label>{i18n.language === 'fr' ? 'Catégories professionnelles' : 'Professional Categories'} * 
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({i18n.language === 'fr' ? 'sélectionnez une ou plusieurs' : 'select one or more'})
                    </span>
                  </Label>
                  <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                    {categories.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        {i18n.language === 'fr' ? 'Chargement des catégories...' : 'Loading categories...'}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {categories.map(cat => (
                          <div key={cat.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`cat-${cat.id}`}
                              checked={(formData.categories || []).includes(cat.id)}
                              onCheckedChange={() => toggleCategory(cat.id)}
                            />
                            <label 
                              htmlFor={`cat-${cat.id}`}
                              className="text-sm cursor-pointer hover:text-[#635BFF]"
                            >
                              {i18n.language === 'fr' ? cat.name_fr : (cat.name_en || cat.name_fr)}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(formData.categories || []).length > 0 && (
                    <p className="text-xs text-[#635BFF] mt-1">
                      {formData.categories.length} {i18n.language === 'fr' ? 'catégorie(s) sélectionnée(s)' : 'category(ies) selected'}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="description_fr">Description (Français) *</Label>
                  <textarea
                    id="description_fr"
                    required
                    rows={4}
                    value={formData.description_fr}
                    onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Décrivez votre entreprise et vos services..."
                  />
                </div>

                <div>
                  <Label htmlFor="description_en">Description (English)</Label>
                  <textarea
                    id="description_en"
                    rows={4}
                    value={formData.description_en}
                    onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="Describe your company and services..."
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-[#0A2540]">
                  {i18n.language === 'fr' ? 'Localisation' : 'Location'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{i18n.language === 'fr' ? 'Pays' : 'Country'} *</Label>
                    <Select value={formData.country} onValueChange={(value) => setFormData({...formData, country: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="city">{i18n.language === 'fr' ? 'Ville' : 'City'} *</Label>
                    <Input
                      id="city"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">{i18n.language === 'fr' ? 'Adresse' : 'Address'}</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="postal_code">{i18n.language === 'fr' ? 'Code postal' : 'Postal Code'}</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-[#0A2540]">
                  {i18n.language === 'fr' ? 'Contact' : 'Contact'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">{i18n.language === 'fr' ? 'Téléphone' : 'Phone'} *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="website">{i18n.language === 'fr' ? 'Site web' : 'Website'}</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Services */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-[#0A2540]">
                  {i18n.language === 'fr' ? 'Types de services' : 'Service Types'}
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['vente', 'location', 'maintenance', 'formation'].map(service => (
                    <div key={service} className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.services.includes(service)}
                        onCheckedChange={() => toggleService(service)}
                      />
                      <Label className="cursor-pointer capitalize">{service}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-[#0A2540]">
                  {i18n.language === 'fr' ? 'Certifications / Normes' : 'Certifications / Standards'}
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['ISO', 'CE', 'NF', 'Autre'].map(cert => (
                    <div key={cert} className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.certifications.includes(cert)}
                        onCheckedChange={() => toggleCertification(cert)}
                      />
                      <Label className="cursor-pointer">{cert}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Company Size */}
              <div>
                <Label>{i18n.language === 'fr' ? 'Taille de l\'entreprise' : 'Company Size'}</Label>
                <Select value={formData.company_size} onValueChange={(value) => setFormData({...formData, company_size: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner' : 'Select'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="artisan">{i18n.language === 'fr' ? 'Artisan' : 'Craftsman'}</SelectItem>
                    <SelectItem value="pme">PME</SelectItem>
                    <SelectItem value="grand_groupe">{i18n.language === 'fr' ? 'Grand groupe' : 'Large Group'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Submit */}
              <div className="pt-6">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-[#635BFF] hover:bg-[#5248d9] text-lg py-6"
                >
                  {loading 
                    ? (i18n.language === 'fr' ? 'Envoi en cours...' : 'Submitting...') 
                    : (i18n.language === 'fr' ? 'Soumettre ma fiche' : 'Submit My Listing')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
